import { IAngularEvent, IComponentOptions, IPromise, IQService, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { v4 as uuidv4 } from "uuid";
import { HotkeysProvider } from "angular-hotkeys";
import dayjs from "@gtmhub/dayjs";
import { IAssignee, AssigneeActions, IAssigneesStoreState, IAssigneesState } from "@gtmhub/assignees";
import { IAccount } from "@gtmhub/core";
import { AnalyticsService, TrackingMetadata } from "@gtmhub/core/analytics";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { InsightService } from "@gtmhub/insights";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { ITeam } from "@gtmhub/teams";
import { INgRedux } from "@gtmhub/state-management";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { InsightRedirectModalService } from "../insight-redirect-modal/insight-redirect-modal";
import {
  GlobalSearchItem,
  GlobalSearchMode,
  GlobalSearchService,
  GlobalSearchItemEnriched,
  getItemReturnState,
  onSearchItemHover,
  searchItemsToEnrichedGlobalSearchItemCollection,
} from "@gtmhub/search";
import { SearchService } from "@gtmhub/shared/services/search.service";
import { ISearchParams } from "@webapp/search/models/search-api.models";
import { SearchCollection } from "@webapp/search/models/search.models";

const bindStateToScope = (state: IAssigneesStoreState) => ({
  assigneesState: state.assignees,
  assigneesMap: state.assignees.map,
});
export interface ISearchScope extends IScope, IExtendedRootScopeService {}

interface IStateParams {
  facet: string;
  searchTerm: string;
  page: number;
}

const SECTIONS_SHOWN = 5;
const ITEMS_SHOWN_IF_CURRENT_FACET = 10;
const ITEMS_SHOWN_IF_NOT_CURRENT_FACET = 4;
const ITEMS_SHOWN_STATE_VIEW = 20;
const PAGINATOR_LIMIT = 5;
const TOP_SCORE = 1;
const DEFAULT_SCORE = 0;

export class GlobalSearchCtrl {
  // ---- Const values ---- //
  private facetToggleOptions: { [facetName: string]: string };
  private facetsOrder: string[];
  private initialized = false;
  private assigneesState: IAssigneesState;

  facetToggleOptionsArray: string[];
  onItemHover: ($event: JQueryEventObject, hovered: boolean) => void;

  // ---- Helpers ---- //
  searchTerm: string;
  itemsLimit = {};
  scoreMap: { [facetName: string]: number };
  assigneesMap: { [key: string]: IAssignee };
  indicators: {
    gettingSearchData: boolean;
    loadingMore: boolean;
    loading: boolean;
  };
  paginator: {
    currentPage: number;
    itemsPerPage: number;
    paginatorLimit: number;
    totalPages?: number;
    itemsTotalCount?: number;
  };
  itemsCount: { [facetName: string]: number } = {};
  itemsPerSection: number;
  account: IAccount;
  lastSearchId: string;
  searchIdTimer: number;

  mode: GlobalSearchMode = "closed";
  itemsShownIfCurrentFacet = ITEMS_SHOWN_IF_CURRENT_FACET;
  stateView: boolean;

  // ---- Bindings ---- //
  currentFacet: string;
  onSearchFocus: ({ isFocus }: { isFocus: boolean }) => void;
  activeUsersOnly: boolean;
  activeTeamsOnly: boolean;

  public static $inject = [
    "$scope",
    "SearchService",
    "GlobalSearchService",
    "$state",
    "$q",
    "UIErrorHandlingService",
    "$stateParams",
    "InsightService",
    "AnalyticsService",
    "AccountResolverService",
    "hotkeys",
    "AssigneeActions",
    "$ngRedux",
    "$rootScope",
    "InsightRedirectModalService",
  ];

  constructor(
    private $scope: ISearchScope,
    private searchService: SearchService,
    private globalSearchService: GlobalSearchService,
    private $state: StateService,
    private $q: IQService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $stateParams: IStateParams,
    private insightService: InsightService,
    private analyticsService: AnalyticsService,
    private accountResolverService: AccountResolverService,
    private hotkeys: HotkeysProvider,
    private assigneeActions: AssigneeActions,
    private $ngRedux: INgRedux,
    private $rootScope: ITraceRootScopeService,
    private insightRedirectModalService: InsightRedirectModalService
  ) {
    this.indicators = {
      gettingSearchData: false,
      loadingMore: false,
      loading: false,
    };

    this.$scope.$on("FeatureFlagUpdated", this.resetComponent);

    // initSearch is behind lazy load when you are using the input search through the UI
    // when you are in the state, it should be loading with state itself - e.g. /search?
    if (this.$state.current.name === "gtmhub.globalSearch") {
      this.initSearch();
    }
  }

  initSearch(): IPromise<void> {
    if (!this.initialized) {
      this.initialized = true;

      const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this);
      this.$scope.$on("$destroy", unsubscribe);

      this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
      return this.$scope.watchUntil(() => this.assigneesState.isFetched).then(() => this.init());
    }
  }

  private init(): void {
    this.account = this.accountResolverService.getAccountData();
    this.onItemHover = onSearchItemHover;
    this.configureHotkeys();

    this.globalSearchService.getFacetToggleOptions({ isSearchV2: false }).then((response) => {
      this.facetToggleOptions = response;

      this.facetToggleOptionsArray = Object.values(this.facetToggleOptions).filter(
        (value) => [this.facetToggleOptions.organization, this.facetToggleOptions.insightsAndInsightboards].indexOf(value) === -1
      );

      this.facetsOrder = Object.keys(this.facetToggleOptions).filter((key) => key !== "everywhere");
      this.scoreMap = this.facetsOrder.reduce((map, facet) => {
        map[facet] = DEFAULT_SCORE;
        return map;
      }, {});

      this.paginator = {
        currentPage: Number(this.$stateParams.page) || 1,
        itemsPerPage: ITEMS_SHOWN_STATE_VIEW,
        paginatorLimit: PAGINATOR_LIMIT,
      };

      if (this.$state.current.name.split(".")[1] === "globalSearch") {
        this.stateView = true;
        if (!this.facetsOrder.includes(this.$stateParams.facet) || !this.$stateParams.searchTerm) {
          this.$state.go("gtmhub.home");
          return;
        }
        this.currentFacet = this.$stateParams.facet;
        this.searchTerm = this.$stateParams.searchTerm || "";
        this.itemsLimit = this.updateItemsLimit();

        this.getSearchData(this.searchTerm);
      } else {
        this.currentFacet = this.currentFacet && this.facetsOrder.includes(this.currentFacet) ? this.currentFacet : "everywhere";
        this.stateView = false;
      }
    });
  }

  onFacetChange($event: IAngularEvent, facet: string, searchTerm: string): void {
    this.$rootScope.traceAction("gs__changed_facet", () => {
      this.currentFacet = Object.keys(this.facetToggleOptions).find((key) => this.facetToggleOptions[key] === facet);
      this.paginator.currentPage = 1;
      this.itemsLimit = this.updateItemsLimit();
      this.$state.go(".", { facet: this.currentFacet }, { notify: false });

      if (searchTerm) {
        this.getSearchData(searchTerm);
      }

      $event.preventDefault();
    });
  }

  onSearchTermChange(searchTerm: string): void {
    this.$rootScope.traceAction("gs__changed_search_term", () => {
      this.paginator.currentPage = 1;

      if (this.$state.current.name === "gtmhub.globalSearch") {
        this.$state.go(".", { searchTerm });
      }

      if (searchTerm) {
        this.changeMode("open");
        this.getSearchData(searchTerm);
      } else {
        this.changeMode("closed");
      }
    });
  }

  onPageChange = (page: number): void => {
    this.$rootScope.traceAction("gs__changed_page", () => {
      this.paginator.currentPage = page;

      this.getSearchData(this.searchTerm);
    });
  };

  viewMoreResults(facet: string, searchTerm: string): void {
    this.$rootScope.traceAction("gs__view_more_results", () => {
      this.sendAnalyticsData("View more clicked");

      this.currentFacet = facet;
      this.itemsLimit = this.updateItemsLimit();
      this.getSearchData(searchTerm, { loadMore: true });
    });
  }

  viewAllResults(facet: string, searchTerm: string): void {
    this.sendAnalyticsData("View all clicked");
    this.$state.go("gtmhub.globalSearch", { facet, searchTerm: searchTerm });
  }

  generateHrefLink(facet: string, item: GlobalSearchItemEnriched<SearchCollection>): string {
    if (facet === "insights" || (facet === "insightsAndInsightboards" && item.collectionName === "insights")) {
      return "";
    }

    const itemReturnState = getItemReturnState(facet, item);
    return this.$state.href(itemReturnState.state, itemReturnState.stateParams);
  }

  selectItem(facet: string, item: GlobalSearchItemEnriched<SearchCollection>, analyticsData: { facetIdx: number; itemIdx: number }): void {
    this.sendTrackEvent(analyticsData);

    if (item.collectionName === "insights" && (facet === "insights" || facet === "insightsAndInsightboards")) {
      this.openInsightRedirectModal(item as GlobalSearchItemEnriched<"insights">);
    }
  }

  filterFacets($ctrl: GlobalSearchCtrl): (facet: string) => boolean {
    return (facet: string) => {
      if (!$ctrl[facet] || !$ctrl[facet].length) {
        return false;
      }

      const facetsToExclude = [
        $ctrl.facetToggleOptions.insights,
        $ctrl.facetToggleOptions.insightboards,
        $ctrl.facetToggleOptions.employees,
        $ctrl.facetToggleOptions.teams,
      ];

      if ($ctrl.facetToggleOptions[$ctrl.currentFacet] === $ctrl.facetToggleOptions.everywhere) {
        return facetsToExclude.indexOf($ctrl.facetToggleOptions[facet]) === -1;
      }

      if (facet === $ctrl.currentFacet) {
        return true;
      }

      return false;
    };
  }

  updateFacetsOrder(scoreMap: { [facetName: string]: number }): (facet: string) => number {
    return (facet: string): number => -scoreMap[facet];
  }

  onSearchFieldFocused(focused: boolean): void {
    this.$rootScope.traceAction("gs__focused_search_field", () => {
      if (this.stateView) {
        return;
      }

      if (focused) {
        this.onSearchFocus({ isFocus: true });
        return;
      }

      if (!this.searchTerm) {
        this.onSearchFocus({ isFocus: false });
      }
    });
  }

  closeSearchMenu(event: JQueryEventObject): void {
    // Do not close the element if the user clicked on part of the search body
    const searchClasses = ["search-actions", "search-field", "facet-picker", "gh-select-toggle", "ui-1_zoom", "dropdown-menu-button", "current"];

    if (!searchClasses.some((c) => event.target.classList.contains(c))) {
      this.changeMode("closed");
    }
  }

  showMoreResults(facet: string, custom: boolean): boolean {
    return (
      !this.isEqualTo(this.currentFacet, facet) &&
      this.itemsCount[facet] > ITEMS_SHOWN_IF_NOT_CURRENT_FACET &&
      (custom
        ? this.isEqualTo(this.facetToggleOptions[facet], this.facetToggleOptions.organization, this.facetToggleOptions.insightsAndInsightboards)
        : !this.isEqualTo(this.facetToggleOptions[facet], this.facetToggleOptions.organization, this.facetToggleOptions.insightsAndInsightboards))
    );
  }

  getTeamNames(item: { teams: ITeam[] }): string {
    if (!item) {
      return "";
    }
    const maxVisibleTeamsCount = 3;
    return item.teams
      .map((team) => team?.name)
      .slice(0, maxVisibleTeamsCount)
      .join(", ");
  }

  onKeydown($event: JQueryEventObject): void {
    const isKeyCodeEnter = $event.keyCode === 13;
    if (isKeyCodeEnter) {
      const selectedEl = document.querySelector<HTMLElement>(".selected-item");
      if (!selectedEl || !selectedEl.classList.contains("item")) {
        this.onSearchTermChange(this.searchTerm);
      } else {
        selectedEl.click();
      }

      return;
    }

    const isKeyCodeUp = $event.keyCode === 38;
    const isKeyCodeDown = $event.keyCode === 40;
    if (isKeyCodeUp || isKeyCodeDown) {
      this.handleUpDownArrowClick($event, isKeyCodeUp ? "up" : "down");
    }
  }

  updateIndicator(params: { isSearchItemLoaded: boolean }): void {
    if (params.isSearchItemLoaded) {
      this.indicators.loading = false;
    }
  }

  private resetComponent = (): void => {
    this.initialized = false;

    const collectionsToBeDeleted = ["tasks", "whiteboards", "kpis"];

    this.deleteCollections(collectionsToBeDeleted);
  };

  private deleteCollections(collectionsToBeDeleted: string[]): void {
    for (const key of collectionsToBeDeleted) {
      if (this[key]) {
        delete this[key];
      }
    }
  }

  private getSearchData(searchTerm: string, options: { loadMore: boolean } = { loadMore: false }): void {
    this.indicators = {
      gettingSearchData: !options.loadMore,
      loadingMore: options.loadMore,
      loading: false,
    };
    // in case the user does not have access to initial facet
    // update value here because bindings do not update on init
    this.currentFacet = this.facetsOrder.includes(this.currentFacet) ? this.currentFacet : "everywhere";

    this.focusSearchField();
    this.updateSearchId();
    this.sendAnalyticsData("Item searched");

    if (this.facetToggleOptions[this.currentFacet] === this.facetToggleOptions.everywhere) {
      this.itemsPerSection = this.stateView ? ITEMS_SHOWN_STATE_VIEW : ITEMS_SHOWN_IF_NOT_CURRENT_FACET;
      this.getAllCollections(searchTerm);
    } else {
      this.itemsPerSection = this.stateView ? ITEMS_SHOWN_STATE_VIEW : ITEMS_SHOWN_IF_CURRENT_FACET;
      // Move current facet to top
      this.resetScoreMap();
      this.scoreMap[this.currentFacet] = TOP_SCORE;

      this.getSingleCollection(searchTerm, this.currentFacet);
    }
  }

  focusSearchField(): void {
    this.$scope.$broadcast("focusGlobalSearchField");
  }

  private getSingleCollection(searchTerm: string, currentFacetName: string): void {
    const queryParams: ISearchParams = {
      skip: this.stateView ? (this.paginator.currentPage - 1) * this.paginator.itemsPerPage : 0,
      take: this.stateView ? this.paginator.itemsPerPage : ITEMS_SHOWN_IF_CURRENT_FACET,
    };

    const searchBody = this.globalSearchService.getSearchBody(searchTerm, currentFacetName, {
      activeUsersOnly: this.activeUsersOnly,
      activeTeamsOnly: this.activeTeamsOnly,
    });

    this.searchService.getSomeSearchData<GlobalSearchItem<SearchCollection>>(searchBody, queryParams).then(
      (searchResults) => {
        this[currentFacetName] = searchItemsToEnrichedGlobalSearchItemCollection(currentFacetName, searchResults.items);
        this.itemsCount[currentFacetName] = searchResults.totalCount;

        this.paginator.totalPages = Math.ceil(searchResults.totalCount / this.paginator.itemsPerPage);
        this.paginator.itemsTotalCount = searchResults.totalCount;

        this.indicators.gettingSearchData = false;
        this.indicators.loadingMore = false;
        this.sendAnalyticsData("Results shown");
      },
      (error) => this.handleIndicatorError(error, `getting ${currentFacetName} data`)
    );

    this.$state.go(".", { page: this.paginator.currentPage, facet: currentFacetName, searchTerm: searchTerm }, { notify: false });
  }

  private changeMode(mode: "open" | "closed"): void {
    this.mode = mode;
  }

  private resetScoreMap(): void {
    for (const key in this.scoreMap) {
      this.scoreMap[key] = DEFAULT_SCORE;
    }
  }

  private getAllCollections(searchTerm: string): void {
    const queryParams: ISearchParams = {
      skip: 0,
      take: ITEMS_SHOWN_IF_NOT_CURRENT_FACET,
    };

    const searchedCollections: string[] = ["okrs", "sessions", "tasks", "organization", "lists", "insightsAndInsightboards", "kpis", "automations", "whiteboards"].filter(
      (collection) => this.facetsOrder.includes(collection)
    );

    const initializationPromises: IPromise<unknown>[] = searchedCollections.map((collection) => this.getOneOfAll(collection, searchTerm, queryParams));

    this.$q.all(initializationPromises).then(() => {
      this.indicators.gettingSearchData = false;
      this.sendAnalyticsData("Results shown");
    });
  }

  private getOneOfAll = (collectionName: string, searchTerm: string, queryParams: ISearchParams) => {
    const searchBody = this.globalSearchService.getSearchBody(searchTerm, collectionName, {
      activeUsersOnly: this.activeUsersOnly,
      activeTeamsOnly: this.activeTeamsOnly,
    });

    return this.searchService.getSomeSearchData<GlobalSearchItem<SearchCollection>>(searchBody, queryParams).then(
      (searchResults) => {
        this[collectionName] = searchItemsToEnrichedGlobalSearchItemCollection(collectionName, searchResults.items);
        this.itemsCount[collectionName] = searchResults.totalCount;
        this.scoreMap[collectionName] = searchResults.maxScore || 1;
      },
      (error) => this.handleIndicatorError(error, `loading ${this.facetToggleOptions[collectionName]}`)
    );
  };

  private handleUpDownArrowClick($event: Event, direction: string): void {
    $event.preventDefault();

    let selectedEl = document.querySelector<HTMLElement>(".selected-item");

    // select first element if no other element was previously selected
    if (!selectedEl || !selectedEl.classList.contains("item")) {
      selectedEl = document.getElementById("0-0");
      selectedEl.classList.add("selected-item");
    } else {
      const currentId = selectedEl.id.split("-");
      let nextEl = document.getElementById(`${currentId[0]}-${+currentId[1] + (direction === "down" ? 1 : -1)}`);
      if (nextEl && nextEl.classList.contains("item")) {
        selectedEl.classList.remove("selected-item");
        nextEl.classList.add("selected-item");
      } else {
        // ==> move to another section
        if (direction === "down") {
          // ==> go to the next section
          nextEl = document.getElementById(`${+currentId[0] + 1}-0`);
        } else {
          // ==> go to the previous section
          for (let itemIdx = this.itemsPerSection + 1; 0 <= itemIdx; itemIdx--) {
            nextEl = document.getElementById(`${+currentId[0] - 1}-${itemIdx}`);
            if (nextEl && nextEl.classList.contains("item")) {
              break;
            }
          }
        }

        if (nextEl && nextEl.classList.contains("item")) {
          selectedEl.classList.remove("selected-item");
          nextEl.classList.add("selected-item");
        } else {
          // End of sections list
          selectedEl.classList.remove("selected-item");

          // ==> go to the first element of the first section
          if (direction === "down") {
            nextEl = document.getElementById("0-0");
          } else {
            // ==> go to the last element of the last section
            for (let idxFacet = SECTIONS_SHOWN - 1; 0 <= idxFacet; idxFacet--) {
              for (let idxItem = this.itemsPerSection + 1; 0 <= idxItem; idxItem--) {
                nextEl = document.getElementById(`${idxFacet}-${idxItem}`);
                if (nextEl && nextEl.classList.contains("item")) {
                  break;
                }
              }
              if (nextEl && nextEl.classList.contains("item")) {
                break;
              }
            }
          }

          nextEl.classList.add("selected-item");
        }
      }
    }
  }

  private sendTrackEvent(analyticsData: { facetIdx: number; itemIdx: number }): void {
    const metaItemFound = this.calculateSearchMeta({
      facet: this.currentFacet,
      facetIdx: analyticsData.facetIdx,
      itemIdx: analyticsData.itemIdx,
      page: this.paginator.currentPage || 1,
      itemsPerPage: this.itemsPerSection,
    });
    this.sendAnalyticsData("Item clicked", metaItemFound);
  }

  private openInsightRedirectModal(item: GlobalSearchItemEnriched<"insights">): void {
    this.insightService.getInsightSafe(item.name).then(
      (insight) => {
        this.insightService.getUsageOnBoards(insight.id).then(
          (boardUsage) => {
            this.insightRedirectModalService.open({ insight, boardUsage });
          },
          (err) => this.handleIndicatorError(err, "loadingBoardUsage")
        );
      },
      (err) => this.handleIndicatorError(err, "loadingBoardUsage")
    );
  }

  private updateItemsLimit(): { [facetName: string]: number } {
    return this.facetsOrder.reduce((acc, facet) => {
      if (this.currentFacet === facet) {
        if (this.stateView) {
          acc[facet] = ITEMS_SHOWN_STATE_VIEW;
        } else {
          acc[facet] = ITEMS_SHOWN_IF_CURRENT_FACET;
        }
      } else {
        acc[facet] = ITEMS_SHOWN_IF_NOT_CURRENT_FACET;
      }

      return acc;
    }, {});
  }

  // when input field is not focused
  private configureHotkeys(): void {
    this.hotkeys.add({
      combo: "up",
      callback: ($event) => {
        this.handleUpDownArrowClick($event, "up");
      },
    });

    this.hotkeys.add({
      combo: "down",
      callback: ($event) => {
        this.handleUpDownArrowClick($event, "down");
      },
    });
  }

  private sendAnalyticsData(eventName: string, meta?: TrackingMetadata): void {
    meta = {
      ...meta,
      account_type: this.account.type,
      state_view: this.stateView,
      search_id: dayjs.utc().isBefore(this.searchIdTimer) ? this.lastSearchId : this.updateSearchId(),
      facet: this.currentFacet,
    };
    this.analyticsService.track(eventName, meta);
  }

  private calculateSearchMeta(metaItemFound: { facet: string; facetIdx: number; itemIdx: number; page: number; itemsPerPage: number }) {
    return {
      ...(metaItemFound.facet === "everywhere" && { facetPosition: metaItemFound.facetIdx + 1 }),
      itemPosition: (metaItemFound.page - 1) * metaItemFound.itemsPerPage + metaItemFound.itemIdx + 1,
    };
  }

  private updateSearchId(): string {
    this.searchIdTimer = dayjs.utc().add(1, "minutes").valueOf();
    this.lastSearchId = uuidv4();
    return this.lastSearchId;
  }

  private isEqualTo<T>(compared: T, ...args: T[]): boolean {
    return args.some((arg) => arg === compared);
  }

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };
}

export const GlobalSearchComponent: IComponentOptions = {
  templateUrl: require("./global-search.jade"),
  controller: GlobalSearchCtrl,
  bindings: {
    currentFacet: "@",
    activeUsersOnly: "<",
    activeTeamsOnly: "<",
    onSearchFocus: "&",
  },
};
